'use client';

import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { drawerRegistry } from '../utils/drawer-registry';

/**
 * React hook for accessing and manipulating the drawer registry.
 * Provides a React-friendly interface to the global drawer state.
 *
 * @returns An object containing:
 *   - isActive: Function to check if a specific drawer is active
 *   - open: Function to open a drawer
 *   - close: Function to close a drawer
 */
export const useDrawerRegistry = () => {
  // Initialize with current state from the registry
  const [activeIds, setActiveIds] = useState<Array<string>>(() => drawerRegistry.getActiveIds());

  // Track drawers opened by this hook instance for cleanup on unmount
  const openedByThisHook = useRef<Set<string>>(new Set());

  // Opens a drawer with the given ID.
  const open = useCallback((multiDrawerTriggerId?: string, event?: SyntheticEvent) => {
    if (drawerRegistry.open(multiDrawerTriggerId, event) && multiDrawerTriggerId) {
      openedByThisHook.current.add(multiDrawerTriggerId);
    }
  }, []);

  // Closes a drawer with the given ID.
  const close = useCallback((multiDrawerTriggerId?: string) => {
    if (multiDrawerTriggerId) {
      openedByThisHook.current.delete(multiDrawerTriggerId);
    }
    drawerRegistry.close(multiDrawerTriggerId);
  }, []);

  // Checks if a specific drawer is currently active.
  const isActive = useCallback(
    (multiDrawerTriggerId: string) => {
      return activeIds.includes(multiDrawerTriggerId);
    },
    [activeIds],
  );

  useEffect(() => {
    // Capture the ref value inside the effect for safe cleanup access
    const openedDrawers = openedByThisHook.current;

    // Subscribe to changes and update state when drawers change
    const unsubscribe = drawerRegistry.subscribe(() => {
      setActiveIds(drawerRegistry.getActiveIds());
    });

    // Clean up subscription when component unmounts
    return () => {
      // Close only drawers that were opened by this hook instance
      openedDrawers.forEach((id) => {
        drawerRegistry.close(id);
      });
      openedDrawers.clear();
      unsubscribe();
    };
  }, []);

  return { isActive, open, close };
};
